import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { reviews } from "@/mocks/reviews";
import usePageMeta from "@/hooks/usePageMeta";
import useFavorites from "@/hooks/useFavorites";
import MovieCard from "@/components/feature/MovieCard";
import MovieHero from "@/pages/detail/components/MovieHero";
import ReviewSection from "@/pages/detail/components/ReviewSection";

export default function Detail() {
  const { movieId } = useParams<{ movieId: string }>();
  const movie = movies.find((m) => m.id === movieId);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [expanded, setExpanded] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState("");

  usePageMeta({
    title: movie
      ? `${movie.title} - 详尽介绍与影评 - 爱的色放电影`
      : "电影详情 - 爱的色放电影",
    description: movie
      ? `查看《${movie.title}》的剧情简介、演员信息及多篇影评，深入了解这部爱情佳作。`
      : "查看爱情电影的剧情简介、演员信息及多篇影评。",
    keywords: movie
      ? `${movie.title},${movie.title}影评,${movie.title}剧情,爱情电影,爱的色放电影`
      : "电影详情,爱情电影,爱的色放电影",
  });

  const movieReviews = useMemo(
    () => reviews.filter((r) => r.movieId === movieId),
    [movieId]
  );

  const related = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter(
        (m) =>
          m.id !== movie.id &&
          (m.genres.some((g) => movie.genres.includes(g)) ||
            m.cast.some((c) => movie.cast.includes(c)))
      )
      .slice(0, 4);
  }, [movie]);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2200);
  };

  const handleShare = (platform: string) => {
    setShareOpen(false);
    const url = window.location.href;
    if (platform === "copy") {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(url).then(
          () => showToast("链接已复制到剪贴板"),
          () => showToast("复制失败，请手动复制地址栏链接")
        );
      } else {
        showToast("复制失败，请手动复制地址栏链接");
      }
      return;
    }
    if (platform === "weibo") {
      window.open(
        `https://service.weibo.com/share/share.php?url=${encodeURIComponent(url)}&title=${encodeURIComponent(
          movie ? `推荐一部爱情电影《${movie.title}》` : "推荐一部爱情电影"
        )}`,
        "_blank",
        "noopener"
      );
      return;
    }
    showToast("请点击右上角「···」，选择分享到微信");
  };

  if (!movie) {
    return (
      <div className="w-full px-4 md:px-10 pt-32 pb-20">
        <div className="max-w-[1440px] mx-auto flex flex-col items-center justify-center text-center py-20 rounded-lg bg-background-50 border border-background-200">
          <i className="ri-movie-2-line text-5xl text-foreground-300"></i>
          <h1 className="mt-4 font-heading font-bold text-xl md:text-2xl text-foreground-950">
            抱歉，没有找到这部电影
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            它可能已被移除，或链接有误
          </p>
          <Link
            to="/category"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回电影分类
          </Link>
        </div>
      </div>
    );
  }

  const favorite = isFavorite(movie.id);

  return (
    <div className="pb-12">
      <MovieHero
        movie={movie}
        isFavorite={favorite}
        onToggleFavorite={() => {
          const now = toggleFavorite(movie.id);
          showToast(now ? "已加入我的收藏" : "已取消收藏");
        }}
        onShare={handleShare}
        shareOpen={shareOpen}
        onToggleShare={() => setShareOpen((v) => !v)}
      />

      <div className="w-full px-4 md:px-10">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* main column */}
          <div className="lg:col-span-2 flex flex-col gap-10">
            {/* 剧情简介 */}
            <section>
              <div className="flex items-center gap-3 mb-5">
                <span className="w-1 h-6 rounded-full bg-primary-500"></span>
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
                  剧情简介
                </h2>
              </div>
              <p
                className={`text-sm md:text-base text-foreground-700 leading-loose ${
                  expanded ? "" : "line-clamp-4"
                }`}
              >
                {movie.summary}
              </p>
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="mt-3 text-sm text-primary-600 hover:text-primary-700 cursor-pointer flex items-center gap-1 whitespace-nowrap"
              >
                {expanded ? "收起" : "展开全文"}
                <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
              </button>
            </section>

            {/* 剧情解析 */}
            <section>
              <div className="flex items-center gap-3 mb-5">
                <span className="w-1 h-6 rounded-full bg-primary-500"></span>
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
                  详细剧情解析
                </h2>
              </div>
              <div className="flex flex-col gap-4">
                {movie.synopsis.map((p, i) => (
                  <p key={i} className="text-sm md:text-base text-foreground-700 leading-loose">
                    {p}
                  </p>
                ))}
              </div>
              <div className="mt-6 rounded-lg bg-accent-50 border border-accent-200 p-5">
                <h3 className="flex items-center gap-2 font-heading font-semibold text-sm text-accent-900 mb-2">
                  <i className="ri-quill-pen-line"></i>
                  幕后花絮
                </h3>
                <p className="text-sm text-foreground-700 leading-relaxed">{movie.behind}</p>
              </div>
            </section>

            {/* 影评聚合 */}
            <ReviewSection reviews={movieReviews} />
          </div>

          {/* side column */}
          <aside className="lg:col-span-1 flex flex-col gap-6">
            <div className="rounded-lg bg-background-50 border border-background-200 p-5">
              <h3 className="font-heading font-semibold text-base text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-price-tag-3-line text-primary-500"></i>
                影片标签
              </h3>
              <div className="flex flex-wrap gap-2">
                {movie.tags.map((t) => (
                  <span
                    key={t}
                    className="px-3 py-1.5 rounded-md bg-secondary-100 text-secondary-900 text-xs"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-lg bg-background-50 border border-background-200 p-5">
              <h3 className="font-heading font-semibold text-base text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-film-line text-primary-500"></i>
                相关推荐
              </h3>
              {related.length === 0 ? (
                <p className="text-sm text-foreground-500">暂无相关推荐</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {related.map((m) => (
                    <Link
                      key={m.id}
                      to={`/detail/${m.id}`}
                      className="group flex gap-3 cursor-pointer"
                    >
                      <div className="w-14 h-20 shrink-0 rounded-md overflow-hidden bg-background-200">
                        <img
                          src={m.poster}
                          alt={`${m.title} 海报`}
                          title={`${m.title} - 相关推荐`}
                          className="w-full h-full object-top object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="min-w-0 flex flex-col justify-center">
                        <p className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                          {m.title}
                        </p>
                        <p className="mt-1 text-xs text-foreground-500">
                          {m.year} · {m.region}
                        </p>
                        <p className="mt-1 text-xs text-accent-600 flex items-center gap-1">
                          <i className="ri-star-fill"></i>
                          {m.rating.toFixed(1)}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/special"
              className="rounded-lg bg-primary-500 text-background-50 p-5 flex flex-col gap-2 cursor-pointer hover:bg-primary-600 transition-colors"
            >
              <i className="ri-article-line text-2xl"></i>
              <span className="font-heading font-semibold text-base">阅读更多主题影评</span>
              <span className="text-xs text-background-100">
                编辑策划的观影片单与深度解析
              </span>
            </Link>
          </aside>
        </div>

        {/* 猜你喜欢 */}
        {related.length > 0 && (
          <div className="max-w-[1440px] mx-auto mt-14">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-1 h-6 rounded-full bg-primary-500"></span>
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
                猜你喜欢
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {related.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </div>
        )}
      </div>

      {toast && (
        <div className="fixed bottom-24 md:bottom-10 left-1/2 -translate-x-1/2 z-[60] px-5 py-3 rounded-md bg-foreground-950/90 text-background-50 text-sm">
          {toast}
        </div>
      )}
    </div>
  );
}